import { useState } from "react";
import { X, Loader2, Search, UserPlus, RotateCcw, Link, User } from "lucide-react";
import { Label } from "../ui/Label";
import { Input } from "../ui/Input";
import { Button } from "../ui/Button";
import { userService } from "../../services/users";
import { toast } from "sonner";
import { AsyncSelect } from "../AsyncSelect";
import { cn } from "../ui/utils";

interface AddSupplierModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: (data: any) => void;
}

export function AddSupplierModal({ isOpen, onClose, onSuccess }: AddSupplierModalProps) {
  const [loading, setLoading] = useState(false);
  const [selectedUser, setSelectedUser] = useState<any | null>(null);
  
  const [formData, setFormData] = useState({
    name: "",
    surname: "",
    code: "",
    email: "",
    cpf: "",
    phone: ""
  });

  if (!isOpen) return null;

  const selectUser = (id: string | null, _label: string, user: any) => {
    if (id === null || id === "nenhum") {
      resetSelection();
      return;
    }
    setSelectedUser(user);
    setFormData({
      ...formData,
      name: user.first_name || user.name || "",
      surname: user.last_name || "",
      email: user.email || "",
      cpf: user.document || "",
      phone: user.phone || "",
      code: user.code || ""
    });
  };

  const resetSelection = () => {
    setSelectedUser(null);
    setFormData({
      name: "",
      surname: "",
      code: "",
      email: "",
      cpf: "",
      phone: ""
    });
  };

  const handleSave = async () => {
    if (!formData.name && !selectedUser) {
      toast.error("Nome é obrigatório");
      return;
    }

    if (!formData.code) {
      toast.error("O código do fornecedor é obrigatório");
      return;
    }

    if (!selectedUser && (!formData.email || !formData.email.trim())) {
      toast.error("O e-mail é obrigatório");
      return;
    }

    try {
      setLoading(true);
      const payload: any = {
        code: formData.code,
      };

      if (selectedUser) {
        payload.user_id = selectedUser.id;
      } else {
        payload.first_name = formData.name;
        payload.last_name = formData.surname;
        payload.email = formData.email.trim();
        payload.document = formData.cpf;
        payload.phone = formData.phone;
        payload.password = "password";
        payload.username = payload.email;
      }

      const response = await userService.create(payload);
      
      toast.success(selectedUser ? "Fornecedor vinculado com sucesso!" : "Fornecedor criado com sucesso!");
      onSuccess(response.data?.data || response.data || response);
      onClose();
      resetSelection();
    } catch (error: any) {
      console.error(error);
      toast.error(error.response?.data?.message || "Erro ao salvar fornecedor");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 flex items-center justify-center z-[100] bg-black/40 backdrop-blur-sm p-4">
      <div className="bg-white rounded-2xl w-full max-w-lg relative border border-gray-100 shadow-2xl animate-in fade-in zoom-in duration-200">
        {/* Header */}
        <div className="p-6 bg-gray-50/50 border-b border-gray-100 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-[#D4C070]/10 flex items-center justify-center text-[#D4C070]">
              {selectedUser ? <Link className="h-5 w-5" /> : <UserPlus className="h-5 w-5" />}
            </div>
            <div>
              <h3 className="text-xl font-bold text-gray-900">
                {selectedUser ? "Vincular Fornecedor" : "Adicionar Fornecedor"}
              </h3>
              <p className="text-[10px] text-gray-500 font-medium uppercase tracking-wider">
                {selectedUser ? "Usuário existente" : "Novo Registro"}
              </p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-full transition-colors text-gray-400">
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="p-6 space-y-6">
          {/* Busca de Usuário - AsyncSelect */}
          {!selectedUser && (
            <div className="space-y-2">
              <Label className="text-[10px] font-bold uppercase text-gray-400 flex items-center gap-2">
                <Search className="h-3 w-3" /> Buscar Usuário Existente
              </Label>
              <AsyncSelect 
                value={selectedUser?.id || ""} 
                onChange={selectUser} 
                fetcher={async (params) => {
                  if (!params.search || params.search.trim().length < 2) return [];
                  return userService.getAll({ ...params, per_page: 20 });
                }} 
                placeholder="Nome, E-mail ou CPF..." 
                getOptionLabel={(u) => {
                  const fullName = [u.first_name, u.last_name].filter(Boolean).join(" ") || u.name || "";
                  return `[${u.code || u.id}] ${fullName}${u.email ? ` (${u.email})` : ""}`;
                }}
                renderOption={(u) => (
                  <div className="flex items-center gap-3 py-1.5 w-full">
                    <div className="flex items-center justify-center min-w-[55px] bg-gray-800 text-white text-[10px] font-bold px-2 py-1 rounded border border-gray-700 uppercase tracking-tighter shadow-sm">
                      {u.code || u.id}
                    </div>
                    <div className="flex flex-col min-w-0 flex-1">
                      <span className="font-bold text-gray-900 truncate">
                        {[u.first_name, u.last_name].filter(Boolean).join(" ") || u.name}
                      </span>
                      {u.email && <span className="text-[10px] text-gray-500 truncate font-medium">{u.email}</span>}
                    </div>
                  </div>
                )}
              />
              <p className="text-[10px] text-gray-400 italic">
                Se o usuário já existe, selecione-o para apenas vincular um código.
              </p>
            </div>
          )}

          {/* Dados Cadastrais */}
          <div className="space-y-4">
            <div className="flex items-center justify-between border-b border-gray-50 pb-2">
              <h4 className="text-xs font-bold text-gray-800 flex items-center gap-2 uppercase">
                <User className="h-4 w-4 text-[#D4C070]" /> Dados do Fornecedor
              </h4>
              {selectedUser && (
                <button 
                  onClick={resetSelection} 
                  className="text-[10px] font-bold text-red-500 hover:text-red-600 flex items-center gap-1 uppercase tracking-tighter"
                >
                  <RotateCcw className="h-3 w-3" /> Resetar Seleção
                </button>
              )}
            </div>

            <div className="grid grid-cols-4 gap-4">
              <div className="col-span-1">
                <Label className="text-[10px] font-bold text-gray-400 uppercase">Código</Label>
                <Input 
                  value={formData.code} 
                  onChange={(e) => setFormData({ ...formData, code: e.target.value.toUpperCase() })} 
                  placeholder="EX: ABC" 
                  className="h-11 border-gray-200 font-black text-[#D4C070] text-lg focus:ring-[#D4C070]/20" 
                />
              </div>
              <div className="col-span-3">
                <Label className="text-[10px] font-bold text-gray-400 uppercase">Nome</Label>
                <Input 
                  value={formData.name} 
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })} 
                  disabled={!!selectedUser} 
                  placeholder="Nome" 
                  className={cn("h-11 border-gray-200", selectedUser && "bg-gray-50 text-gray-500")} 
                />
              </div>
            </div>

            {!selectedUser && (
              <>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-1">
                    <Label className="text-[10px] font-bold text-gray-400 uppercase">Sobrenome</Label>
                    <Input value={formData.surname} onChange={(e) => setFormData({ ...formData, surname: e.target.value })} placeholder="Sobrenome" className="h-11 border-gray-200" />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-[10px] font-bold text-gray-400 uppercase">Celular</Label>
                    <Input value={formData.phone} onChange={(e) => setFormData({ ...formData, phone: e.target.value })} placeholder="(00) 00000-0000" className="h-11 border-gray-200" />
                  </div>
                </div>

                <div className="space-y-1">
                  <Label className="text-[10px] font-bold text-gray-400 uppercase">Email <span className="text-red-500">*</span></Label>
                  <Input type="email" required value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })} placeholder="email@exemplo.com" className="h-11 border-gray-200" />
                </div>

                <div className="space-y-1">
                  <Label className="text-[10px] font-bold text-gray-400 uppercase">CPF / CNPJ</Label>
                  <Input value={formData.cpf} onChange={(e) => setFormData({ ...formData, cpf: e.target.value })} placeholder="000.000.000-00" className="h-11 border-gray-200" />
                </div>
              </>
            )}

            {selectedUser && (
              <div className="bg-gray-50 p-4 rounded-xl border border-gray-100 flex flex-col gap-1">
                <p className="text-[10px] font-bold text-gray-400 uppercase">Vínculo Ativo</p>
                <p className="text-sm font-bold text-gray-900">{formData.name} {formData.surname}</p>
                <p className="text-xs text-gray-500 font-medium">{formData.email} • {formData.cpf}</p>
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="p-6 bg-gray-50/50 border-t border-gray-100 flex justify-end gap-3">
          <Button variant="ghost" onClick={onClose} disabled={loading} className="text-gray-500 font-bold uppercase text-[10px] tracking-widest">
            Cancelar
          </Button>
          <Button 
            onClick={handleSave} 
            className="bg-[#D4C070] hover:bg-[#D4C070]/90 text-white px-8 font-bold shadow-lg shadow-[#D4C070]/20 min-w-[140px]" 
            disabled={loading}
          >
            {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : (selectedUser ? "VINCULAR AGORA" : "CRIAR REGISTRO")}
          </Button>
        </div>
      </div>
    </div>
  );
}
